import React, { useEffect, useState } from 'react'
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Icon } from '@iconify/react';
import { Button } from '@mui/material'
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { CCol, CForm, CFormInput, CFormLabel, CRow } from '@coreui/react';
import { useAuth } from '../contexts/AuthContext';
import { useChoice } from '../contexts/ChoicesContext';
import CInput from './CInput';
import ProductSelection from './ProductSelection';

const AddChoice = ({ id, choiceDetails, editMode, editChoiceId, setEditChoiceId, setEditMode, choiceVisible, setChoiceVisible, setChoiceDetails, fetchChoices, canCreate, canUpdate, canDelete }) => {
    const { createChoice, updateChoice, getSingleChoice, deleteChoice } = useChoice()
    const { restaurantId } = useAuth();

    const [validated, setValidated] = useState(false);
    const [productsValidated, setProductsValidated] = useState(false)

    const handleClose = () => {
        setChoiceVisible(false);
        setEditMode(false);
        setEditChoiceId(null);
        setChoiceDetails({
            name: '',
            plu: '',
            products: [],
            visible: "",
            image: "",
            restaurant_id: restaurantId,
            multi_select: 0,
            min: '',
            max: '',
            no_max: 0
        });
    };

    const handleDelete = async () => {
        try {
            const confirmed = await confirmDelete();

            if (confirmed) {
                const data = await deleteChoice(id);
                if (data.error === false) {
                    handleClose();
                    fetchChoices()
                }
            }
        } catch (error) {
            console.error('Error during delete operation', error);
        }
    };

    const confirmDelete = async () => {
        return new Promise((resolve) => {
            confirmDialog({
                message: 'Are you sure you want to delete this choice?',
                header: 'Delete Confirmation',
                icon: 'pi pi-info-circle',
                position: 'top',
                accept: () => resolve(true),
                reject: () => resolve(false),
            });
        });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setChoiceDetails({ ...choiceDetails, image: reader.result })
            };
            reader.readAsDataURL(file);
        }
    };

    const fetchSingleChoice = async () => {
        try {
            const data = await getSingleChoice(id);
            if(data){
                setChoiceDetails(data);
            }
        } catch (error) {
            console.error('Error fetching single choice', error);
        }
    };

    useEffect(() => {
        if (editMode && editChoiceId) fetchSingleChoice();
    }, [editMode, editChoiceId]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if ((editMode && !canUpdate) || (!editMode && !canCreate)) return;

        const form = e.currentTarget;
        setValidated(true);
        choiceDetails.products.length > 0 ? setProductsValidated(false) : setProductsValidated(true)

        if (form.checkValidity() === false) {
            e.stopPropagation();

            const firstInvalidInput = form.querySelector(':invalid');
            if (firstInvalidInput) {
                firstInvalidInput.focus();
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } else {
            try {
                if (choiceVisible && restaurantId && choiceDetails.products.length > 0) {
                    const selectedProducts = choiceDetails.products.length > 0 ? choiceDetails.products.map(product => product.value) : []
                    const data = await createChoice({ ...choiceDetails, restaurant_id: restaurantId, products: selectedProducts })

                    if (!data.error) {
                        fetchChoices(data.choice)
                        handleClose()
                    }
                } else if (editMode && editChoiceId && restaurantId && choiceDetails.products.length > 0) {
                    const selectedProducts = choiceDetails.products.length > 0 ? choiceDetails.products.map(product => product.value) : []
                    const data = await updateChoice(id, { ...choiceDetails, restaurant_id: restaurantId, products: selectedProducts })
                    if (!data.error) {
                        handleClose()
                        fetchChoices()
                    }
                }
            } catch (error) {
                console.log(error)
            }
        }
    }

    return (
        <div>
            <ConfirmDialog />
            <Dialog draggable={false} header={`${choiceVisible ? 'Add Choice' : 'Edit Choice'}`} visible={choiceVisible || (editMode && editChoiceId !== null)} style={{ width: `35vw` }} onHide={handleClose}>
                <CForm onSubmit={handleSubmit} noValidate validated={validated}>
                    <div className='fields-container'>
                        <CRow className="mb-3 mt-3">
                            <CInput
                                label="Name"
                                type="text"
                                className='d-flex'
                                labelClassName='w-50'
                                value={choiceDetails.name}
                                onChange={(value) => setChoiceDetails({ ...choiceDetails, name: value })}
                                required={true}
                                errorMessage='Name is required.'
                            />
                        </CRow>
                        <CRow className="mb-3 mt-3">
                            <CInput
                                label="PLU"
                                type="text"
                                className='d-flex'
                                labelClassName='w-50'
                                value={choiceDetails.plu}
                                onChange={(value) => setChoiceDetails({ ...choiceDetails, plu: value })}
                                required={true}
                                errorMessage='PLU is required.'
                            />
                        </CRow>
                        <CRow className='mb-4'>
                            <ProductSelection
                                className='d-flex'
                                labelClassName='w-50'
                                multiSelect={true}
                                value={choiceDetails.products}
                                onChange={(value) => setChoiceDetails({ ...choiceDetails, products: value })}
                                required={true}
                                error={productsValidated}
                            />
                        </CRow>
                        <CRow className='mb-4'>
                            <CCol lg={4}>
                                <CFormLabel className="form-label">Visible</CFormLabel>
                            </CCol>
                            <CCol lg={8} className='px-1'>
                                <Checkbox
                                    checked={choiceDetails.visible === 1}
                                    onChange={(event) => setChoiceDetails({ ...choiceDetails, visible: event.target.checked ? 1 : 0 })}
                                />
                            </CCol>
                        </CRow>
                        <CRow className='mb-4'>
                            <CCol lg={4}>
                                <CFormLabel className="form-label">Multi Select</CFormLabel>
                            </CCol>
                            <CCol lg={8} className='px-1'>
                                <Checkbox
                                    checked={choiceDetails.multi_select === 1}
                                    onChange={(event) => setChoiceDetails({ ...choiceDetails, multi_select: event.target.checked ? 1 : 0 })}
                                />
                            </CCol>
                        </CRow>
                        {
                            choiceDetails.multi_select === 1 && (
                                <CRow className='mb-4'>
                                    <CCol lg={4}>
                                        <CFormLabel>Min / Max</CFormLabel>
                                    </CCol>
                                    <CCol lg={2} className='min px-1'>
                                        <CFormInput
                                            type={'text'}
                                            value={choiceDetails.min}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                const sanitizedValue = newValue.replace(/[^0-9.]/g, '');
                                                if (sanitizedValue !== '') {
                                                    const numericValue = parseFloat(sanitizedValue);
                                                    setChoiceDetails({ ...choiceDetails, min: numericValue });
                                                } else {
                                                    setChoiceDetails({ ...choiceDetails, min: '' });
                                                }
                                            }}
                                            feedbackInvalid='Please enter a valid number.'
                                        />

                                    </CCol>
                                    <CCol lg={2}>
                                        <CFormInput
                                            type={'text'}
                                            value={choiceDetails.max}
                                            disabled={choiceDetails.no_max === 1}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                const sanitizedValue = newValue.replace(/[^0-9.]/g, '');
                                                if (sanitizedValue !== '') {
                                                    const numericValue = parseFloat(sanitizedValue);
                                                    setChoiceDetails({ ...choiceDetails, max: numericValue });
                                                } else {
                                                    setChoiceDetails({ ...choiceDetails, max: '' });
                                                }
                                            }}
                                            feedbackInvalid='Please enter valid number.'
                                        />
                                    </CCol>
                                    <CCol lg={4} className='d-flex'>
                                        <Checkbox
                                            checked={choiceDetails.no_max === 1}
                                            onChange={(event) => {
                                                if (event.target.checked === true) {
                                                    setChoiceDetails({ ...choiceDetails, max: "", no_max: 1 })
                                                } else {
                                                    setChoiceDetails({ ...choiceDetails, no_max: 0 })
                                                }
                                            }}
                                        />
                                        <CFormLabel className="form-label ms-2">No max value</CFormLabel>
                                    </CCol>
                                </CRow>
                            )
                        }
                        <CRow className="mb-3">
                            <CCol lg={4}>
                                <CFormLabel className="form-label">Image</CFormLabel>
                            </CCol>
                            <CCol lg={8} style={{ position: 'relative' }} className='px-1'>
                                <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} id="fileInput" />
                                <label htmlFor="fileInput">
                                    <Card
                                        className="create-card"
                                        header={choiceDetails.image ? <LazyLoadImage src={choiceDetails.image || 'defaultImageURL'} alt="User" className='create-img' /> :
                                            <Icon icon="ep:food" style={{ color: 'black' }} height={100} width={100} />}
                                    />
                                </label>
                                {
                                    choiceDetails.image &&
                                    <Icon
                                        icon="mdi:close"
                                        className='close-btn'
                                        height={15}
                                        width={15}
                                        onClick={() => setChoiceDetails({ ...choiceDetails, image: '' })}
                                    />
                                }
                            </CCol>
                        </CRow>
                    </div>
                    <div className={`d-flex button-container ${(editMode && editChoiceId && canDelete) ? 'justify-content-between' : 'justify-content-end'}`}>
                        {
                            (editMode && editChoiceId && canDelete) && (
                                <div>
                                    <Button color='error' variant="contained" onClick={handleDelete} >
                                        Delete
                                    </Button>
                                </div>
                            )
                        }
                        <div className='d-flex'>
                            <Button onClick={handleClose} fullWidth variant="contained" color="inherit" >
                                Cancel
                            </Button>
                            {((!editMode && canCreate) || (editMode && canUpdate)) && (
                                <Button fullWidth variant="contained" color="primary" type="submit" className="ms-3">
                                    Submit
                                </Button>
                            )}
                        </div>
                    </div>
                </CForm>
            </Dialog>
        </div>
    )
}

export default AddChoice
